import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [errors, setErrors] = useState({
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // List of common personal email domains to reject
  const personalEmailDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'me.com', 'live.com', 'msn.com', 'ymail.com',
    'rocketmail.com', 'mail.com', 'protonmail.com', 'tutanota.com',
    'zoho.com', 'fastmail.com', 'hushmail.com', 'guerrillamail.com',
    'tempmail.org', '10minutemail.com', 'mailinator.com'
  ];

  const validateWorkEmail = (email: string): boolean => {
    if (!email) return false;
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;
    
    // Extract domain from email
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    
    // Check if it's a personal email domain
    return !personalEmailDomains.includes(domain);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData({...formData, email});
    
    // Clear previous errors
    setErrors({...errors, email: ''});
    
    // Validate email if it's not empty
    if (email && !validateWorkEmail(email)) {
      const domain = email.split('@')[1]?.toLowerCase();
      if (personalEmailDomains.includes(domain)) {
        setErrors({...errors, email: 'Please use your work email address. Personal email addresses are not accepted.'});
      } else if (!email.includes('@') || !email.includes('.')) {
        setErrors({...errors, email: 'Please enter a valid email address.'});
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate work email before submission
    if (!validateWorkEmail(formData.email)) {
      setErrors({...errors, email: 'Please use your work email address. Personal email addresses are not accepted.'});
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // EmailJS configuration - Replace these with your actual EmailJS credentials
      const serviceId = 'service_6xwjpuf';
      const templateId = 'template_07t0aj9';
      const publicKey = 'rL5GacUiM5649BnKA';

      // Template parameters for EmailJS
      const templateParams = {
        to_email: 'info@hatomconsulting.com',
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company,
        message: formData.message,
        subject: `New Contact Form Submission from ${formData.name}`,
        reply_to: formData.email
      };

      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      });
      
      setSubmitStatus('success');
    } catch (error) {
      console.error('Email sending error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-serif text-navy mb-6">Tell us how we can help</h2>
      
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-800">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">Sorry, there was an error sending your message. Please try again or email us directly at info@hatomconsulting.com</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal focus:border-teal"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <span className="font-semibold">Work email</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={handleEmailChange}
              className={`w-full px-4 py-2 border rounded-md focus:ring-teal focus:border-teal ${
                errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              required
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Please use your company email address. Personal emails (Gmail, Yahoo, etc.) are not accepted.
            </p>
          </div>

          {/* Company Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <span className="font-semibold">Company</span>
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal focus:border-teal"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Message Field */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">How can we help?</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-teal focus:border-teal"
              placeholder="I'm interested in the services of Hatom Consulting. I'd like to know more about..."
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Submit Section */}
          <div className="pt-4">
            <p className="text-sm text-gray-600 mb-4">
              You can also email us at <a href="mailto:info@hatomconsulting.com" className="text-teal hover:underline">info@hatomconsulting.com</a>
            </p>
            <button
              type="submit"
              disabled={isSubmitting || !!errors.email}
              className={`w-full py-3 px-6 rounded-md transition-colors ${
                isSubmitting || !!errors.email
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-teal text-white hover:bg-teal-600'
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
